import { Component, type ReactNode, type ErrorInfo } from 'react'
import { AlertTriangle, RefreshCw, Trash2 } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  isRepeatError: boolean // M5: 반복 에러 감지
}

// M5: 무한 새로고침 방지 상수
const ERROR_COUNT_KEY = 'tdl_error_boundary_count'
const ERROR_TIMESTAMP_KEY = 'tdl_error_boundary_ts'
const MAX_ERRORS_BEFORE_RESET = 3
const ERROR_WINDOW_MS = 5 * 60 * 1000 // 5분

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, isRepeatError: false }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo)

    // Structured error logging to localStorage (max 20 entries)
    try {
      const entry = {
        timestamp: new Date().toISOString(),
        message: error.message,
        stack: error.stack?.slice(0, 500),
        componentStack: errorInfo.componentStack?.slice(0, 500),
      }
      const key = 'tdl_error_log'
      const existing = JSON.parse(localStorage.getItem(key) || '[]')
      existing.push(entry)
      if (existing.length > 20) existing.splice(0, existing.length - 20)
      localStorage.setItem(key, JSON.stringify(existing))
    } catch {
      // Ignore storage errors
    }

    // M5: 반복 에러 감지 — 5분 내 3회 이상이면 무한 루프로 판단
    try {
      const now = Date.now()
      const lastTs = Number(sessionStorage.getItem(ERROR_TIMESTAMP_KEY) || '0')
      let count = Number(sessionStorage.getItem(ERROR_COUNT_KEY) || '0')

      if (now - lastTs > ERROR_WINDOW_MS) {
        // 윈도우 초과 — 카운터 리셋
        count = 1
      } else {
        count++
      }

      sessionStorage.setItem(ERROR_COUNT_KEY, String(count))
      sessionStorage.setItem(ERROR_TIMESTAMP_KEY, String(now))

      if (count >= MAX_ERRORS_BEFORE_RESET) {
        this.setState({ isRepeatError: true })
      }
    } catch {
      // sessionStorage 접근 불가 시 무시
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, isRepeatError: false })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleClearData = () => {
    try {
      // 에러 카운터 리셋
      sessionStorage.removeItem(ERROR_COUNT_KEY)
      sessionStorage.removeItem(ERROR_TIMESTAMP_KEY)
      // localStorage 캐시 정리
      localStorage.removeItem('tdl_error_log')
      // IndexedDB 삭제
      indexedDB.deleteDatabase('tdl_database')
    } catch {
      // 무시
    }
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-secondary p-4">
          <div className="text-center max-w-md">
            <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-heading mb-2">
              오류가 발생했습니다
            </h1>
            <p className="text-sm text-caption mb-6 leading-relaxed">
              {this.state.error?.message || '예상치 못한 오류가 발생했습니다. 다시 시도해 주세요.'}
            </p>

            {this.state.isRepeatError ? (
              // M5: 반복 에러 시 데이터 초기화 옵션 표시
              <div>
                <p className="text-xs text-warning-600 dark:text-warning-400 mb-4">
                  동일한 오류가 반복되고 있습니다. 로컬 데이터를 초기화하면 해결될 수 있습니다.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={this.handleRetry}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium bg-surface-muted text-heading hover:bg-[var(--active-bg)] transition-colors"
                  >
                    다시 시도
                  </button>
                  <button
                    onClick={this.handleClearData}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors flex items-center gap-1.5"
                  >
                    <Trash2 className="w-4 h-4" />
                    데이터 초기화
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-3 justify-center">
                <button
                  onClick={this.handleRetry}
                  className="px-4 py-2.5 rounded-lg text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors"
                >
                  다시 시도
                </button>
                <button
                  onClick={this.handleReload}
                  className="px-4 py-2.5 rounded-lg text-sm font-medium bg-surface-muted text-heading hover:bg-[var(--active-bg)] transition-colors flex items-center gap-1.5"
                >
                  <RefreshCw className="w-4 h-4" />
                  새로고침
                </button>
              </div>
            )}
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
